import { getDisplayAssetCode } from '@altify/utils';
import { ProductsWithHoldingsAndRates } from 'hooks/products/useProducts';
import { useProductsGraphs } from 'hooks/products/useGraphs';
import { QuestionTemplate } from 'services/generated';
import { useGetQuestionTemplatesQuery } from 'store/slices/references-api';
import { AiInsightsIcon } from 'assets/icons/AiInsightsIcon';
import { getPercentageChangeForTimeFrameKey } from 'app/_authenticated/products/explore/components/ExploreGainLoss';
import { TOP_PRODUCTS } from '../constants/assets';

/**
 * Parses and formats AI Insights questions based on the page context
 */
export function parseAiInsightsQuestions(
  pageName: 'Explore' | 'Assets' | 'Portfolio',
  products: ProductsWithHoldingsAndRates,
  productGraph: ReturnType<typeof useProductsGraphs>,
  productCode?: string,
  productType?: string,
  hasAccessToBetaFeatures?: boolean,
) {
  const { data: questionTemplates } = useGetQuestionTemplatesQuery({
    type: pageName,
  });
  var questions = [];
  const orderedQuestions =
    questionTemplates?.slice().sort((a, b) => a.order - b.order) || [];

  if (pageName === 'Portfolio') {
    questions = orderedQuestions.map(q => ({
      value: q.order,
      label: q.question,
    }));
  } else if (pageName === 'Assets' && productCode && productType) {
    questions = parseAssetPageQuestions(
      productCode,
      productType,
      orderedQuestions,
      productGraph,
    );
  } else {
    questions = parseExploreQuestions(orderedQuestions, products, productGraph);
  }
  questions = [
    hasAccessToBetaFeatures
      ? {
          value: 0,
          label: 'Start a new chat',
          icon: AiInsightsIcon,
          order: 0,
        }
      : null,
    ...questions,
  ];

  return questions.filter(q => q !== null) ?? [];
}

/**
 * Parses questions for the asset detail page
 */
function parseAssetPageQuestions(
  productCode: string,
  productType: string,
  questions: QuestionTemplate[],
  productGraph: ReturnType<typeof useProductsGraphs>,
) {
  //if (productType === ActiveProductDtoTypeEnum.Locked) return [];
  const assetDirection =
    (getPercentageChangeForTimeFrameKey(productCode, productGraph) ?? 0) >= 0
      ? 'up'
      : 'down';

  let categoryQuestions = questions.filter(q => q.assetType === productType);
  if (categoryQuestions.length === 0) {
    categoryQuestions = questions.filter(q => !q.assetType);
  }

  return categoryQuestions.map(q => ({
    label: q.question
      .replace('{{asset}}', getDisplayAssetCode(productCode) || '')
      .replace('{{assetDirection}}', assetDirection),
    value: q.order,
  }));
}

/**
 * Parses questions for the explore page
 */
function parseExploreQuestions(
  questions: QuestionTemplate[],
  products: ProductsWithHoldingsAndRates,
  productGraph: ReturnType<typeof useProductsGraphs>,
) {
  if (!products?.length || !productGraph.graphs?.length) {
    return [];
  }

  const topMovingAsset = products
    .filter(product => TOP_PRODUCTS.includes(product.code))
    .sort((a, b) => {
      const changeA =
        getPercentageChangeForTimeFrameKey(a.code, productGraph) ?? 0;
      const changeB =
        getPercentageChangeForTimeFrameKey(b.code, productGraph) ?? 0;
      return changeB - changeA;
    })[0];

  const topMovingAssetDirection =
    (getPercentageChangeForTimeFrameKey(
      topMovingAsset?.code ?? '',
      productGraph,
    ) ?? 0) >= 0
      ? 'up'
      : 'down';

  return questions.map(q => ({
    value: q.order,
    label: q.question
      .replace(
        '{{topMovingAsset}}',
        getDisplayAssetCode(topMovingAsset?.code) || '',
      )
      .replace('{{topMovingAssetDirection}}', topMovingAssetDirection),
    icon: AiInsightsIcon,
    order: q.order,
  }));
}
